import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { Dialog as DialogPrimitive, VisuallyHidden } from 'radix-ui';
import type { CatalogSearchResult } from '../../../data/catalogSearch';
import { CatalogSearch } from './CatalogSearch';
import { m } from '../../../paraglide/messages';

interface CatalogSearchContextValue {
  /** opens the command palette from anywhere under the provider */
  open: () => void;
}

const CatalogSearchContext = createContext<CatalogSearchContextValue>({
  open: () => {},
});

/** Opens the catalog command palette (Cmd/Ctrl-K equivalent). Safe to call
 *  outside the provider - it no-ops until a provider is mounted. */
export const useCatalogSearchTrigger = (): CatalogSearchContextValue =>
  useContext(CatalogSearchContext);

export interface CatalogSearchProviderProps {
  children: ReactNode;
  /** forwarded to the palette: overrides navigate-to-detail selection */
  onSelect?: (result: CatalogSearchResult) => void;
}

/**
 * Mounts the global catalog command palette and a Cmd/Ctrl-K listener, and
 * exposes an `open()` trigger via context. The dialog renders nothing until
 * opened, so it is SSR/prerender-safe (deterministic closed markup) and adds no
 * catalog fetch until the user summons it.
 */
export const CatalogSearchProvider = ({ children, onSelect }: CatalogSearchProviderProps) => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <CatalogSearchContext.Provider value={{ open: () => setOpen(true) }}>
      {children}
      <DialogPrimitive.Root open={open} onOpenChange={setOpen}>
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/40 backdrop-blur-[2px] data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
          <DialogPrimitive.Content
            aria-describedby={undefined}
            className="fixed top-[4vh] left-1/2 z-50 w-[calc(100%-1.5rem)] max-w-160 -translate-x-1/2 overflow-hidden rounded-14 border border-line bg-surface shadow-[0_1px_2px_rgba(10,10,10,0.06),0_32px_72px_-20px_rgba(10,14,40,0.42)] duration-150 data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 sm:top-[12vh] sm:w-[calc(100%-2rem)]"
          >
            <VisuallyHidden.Root asChild>
              <DialogPrimitive.Title>{m.search_dialog_title()}</DialogPrimitive.Title>
            </VisuallyHidden.Root>
            <CatalogSearch onClose={() => setOpen(false)} onSelect={onSelect} />
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>
    </CatalogSearchContext.Provider>
  );
};
